/**
 * CanvaLoop brand logo: official card-fan mark + lowercase wordmark.
 *
 * Brand kit locked 2026-07-11 (source: Remotion brand film 001,
 * `canvaloop-mark.svg` / lockup spec). Three fanned post cards: front
 * card carries the aurora gradient (#C53FFF to #FF6B9D to #FF8E53, 135deg),
 * wings in periwinkle (#6E8BFF) and sky (#4DD4FF) at 82% opacity.
 * Wordmark: `canvaloop` lowercase, Sora SemiBold 600, tracking -0.035em.
 *
 * Variants:
 *   - "wordmark" (default): mark + "canvaloop" for nav / footer
 *   - "mark": just the card-fan mark, square aspect, favicon contexts
 *   - "stack": mark above wordmark, for splash / login
 */

import React from "react";

export interface BrandLogoProps {
  variant?: "wordmark" | "mark" | "stack";
  size?: number;        // height in px (mark dim, or text height for wordmark)
  className?: string;
  /** Kept for API compatibility. The card-fan mark is static. */
  paused?: boolean;
}

/**
 * Card-fan mark: inline copy of the kit's `canvaloop-mark.svg` with
 * per-instance gradient ids (several logos can mount on one page).
 * Don't rotate, don't recolor the front gradient (brand kit rule).
 */
const CardFanMark: React.FC<{ size: number }> = ({ size }) => {
  const id = React.useId().replace(/:/g, "");
  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      fill="none"
      style={{ display: "inline-block", verticalAlign: "middle" }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`clf-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#C53FFF" />
          <stop offset="55%" stopColor="#FF6B9D" />
          <stop offset="100%" stopColor="#FF8E53" />
        </linearGradient>
        <linearGradient id={`clwl-${id}`} x1="0" y1="0" x2="0.9" y2="1">
          <stop offset="0%" stopColor="#6E8BFF" />
          <stop offset="100%" stopColor="#A2B4FF" />
        </linearGradient>
        <linearGradient id={`clwr-${id}`} x1="1" y1="0" x2="0.1" y2="1">
          <stop offset="0%" stopColor="#4DD4FF" />
          <stop offset="100%" stopColor="#8DE3FF" />
        </linearGradient>
      </defs>
      {/* left wing */}
      <g
        transform="rotate(-15 60 96) translate(-15 1.5) translate(60 63) scale(0.94) translate(-60 -63)"
        fill={`url(#clwl-${id})`}
        opacity="0.82"
      >
        <rect x="34" y="30" width="52" height="66" rx="13" ry="13" />
      </g>
      {/* right wing */}
      <g
        transform="rotate(15 60 96) translate(15 1.5) translate(60 63) scale(0.94) translate(-60 -63)"
        fill={`url(#clwr-${id})`}
        opacity="0.82"
      >
        <rect x="34" y="30" width="52" height="66" rx="13" ry="13" />
      </g>
      {/* front card */}
      <g fill={`url(#clf-${id})`}>
        <rect x="34" y="30" width="52" height="66" rx="13" ry="13" />
      </g>
    </svg>
  );
};

/** Wordmark spec: Sora SemiBold, lowercase, tracking -0.035em. */
const WORDMARK_FONT = "'Sora', var(--font-display), ui-sans-serif, sans-serif";

export const BrandLogo: React.FC<BrandLogoProps> = ({
  variant = "wordmark",
  size = 22,
  className,
}) => {
  if (variant === "mark") {
    return (
      <span className={className} style={{ display: "inline-flex", alignItems: "center" }}>
        <CardFanMark size={size * 1.35} />
      </span>
    );
  }

  if (variant === "stack") {
    return (
      <span
        className={className}
        style={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
        }}
      >
        <CardFanMark size={size * 2.4} />
        <span style={{
          fontFamily: WORDMARK_FONT,
          fontSize: size * 1.4,
          fontWeight: 600,
          letterSpacing: "-0.035em",
          color: "var(--text-primary)",
          lineHeight: 1,
        }}>
          canvaloop
        </span>
      </span>
    );
  }

  // wordmark
  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        fontFamily: WORDMARK_FONT,
        fontSize: size,
        fontWeight: 600,
        letterSpacing: "-0.035em",
        color: "var(--text-primary)",
        lineHeight: 1,
      }}
    >
      {/* Mark sits slightly larger than cap height: the kit SVG carries
          its own internal padding, so the visual weight matches the text. */}
      <CardFanMark size={size * 1.45} />
      <span>canvaloop</span>
    </span>
  );
};

export default BrandLogo;
